import type { PreviewDivisionRowDto, PreviewDutyDto } from '@pams/shared';
import { PREVIEW_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C1h §4] THE FORCE TABLE IS A MATRIX, BECAUSE A REAL PA ORDER IS ONE.
 *
 *  §4: "THE CURRENT PREVIEW TRANSPOSED THE ORDER. A real PA order is a table: supplying units
 *  down the side, post types across the top, a Total row beneath." It rendered a LIST — one
 *  block per post, with its addressees indented underneath — which is the same data read the
 *  other way up from the paper the operator is holding.
 *
 *  ⚠️ §4a — IT IS THE CREATE-ARRANGEMENT FORCE GRID, READ BACK. Same orientation, same source
 *  rows in creation order, same post columns, so the operator already knows how to read it.
 *  ⚠️ **BUT IT DOES NOT REUSE `ForceGrid`.** That component is an EDITOR: `InputNumber` per
 *  cell, a paste handler, an add-source row, a `Form` binding and a `fromGrid` write path. A
 *  read-only projection shares its LAYOUT and none of its behaviour, and threading a
 *  `readOnly` through all of it would put a write path one prop away from a preview.
 *
 *  ⚠️ [amended C3a §5a] §4c's TOTAL ROW IS GONE, AND THE COUNT MOVED INTO THE COLUMN HEADER.
 *
 *  [C1h §4c] put the total BENEATH — a deliberate divergence from `ForceGrid`, which puts its
 *  `TOTAL REQUIRED` at the top, on the argument that a sum belongs after the things it sums. §5a
 *  asks for `(N of M named)` beside the post HEADING, and once it is there the footer row states
 *  the same number a second time — which is §4c's own "no extra counts" and [C1h §4b]'s "the
 *  number appears once", both of which outrank where a sum conventionally goes.
 *
 *  ⚠️ THE CELLS KEEP THE BARE `n of m`. A grid in which every cell reads "…named" is unreadable,
 *  and the word is already in the heading the column is read under.
 *
 *  ⚠️ §4b/§4f — EACH CELL IS `n of m` AND THE NUMBER APPEARS ONCE. The list rendered a post's
 *  total and then, for a single-addressee post, the SAME figure again in grey one line down.
 *  A matrix has one cell per intersection, so the duplicate has nowhere to be.
 *
 *  ⚠️ §4g — A POST WITH A REQUIREMENT AND NO SOURCE YET IS AN EMPTY COLUMN, not a row reading
 *  `No division has been asked for this yet`. Its cells are blank and its heading still states
 *  the requirement.
 *
 *  ⚠️ [amended C4a §3] A POST THAT ASKS FOR NOTHING AT ALL — required 0, no source, no name — NO
 *  LONGER ARRIVES. The service drops it (`lineAsksForNothing`), because `(0 of 0 named)` on every
 *  untouched policy column was noise. Header and body both loop `duty.posts`, which is what hides
 *  the two together; `ArrangementPreviewModal.test.tsx` asserts their spans still match.
 *
 *  ⚠️ §4h — AND A DELEGATED ORDER SHOWS MORE ROWS THAN THE BUILDER, WHICH IS CORRECT. [C1f]
 *  splits an allocation line, so `CD · Any` becomes `CD · Tala` and `CD · Bowbazar`; the
 *  payload carries the unit for exactly this reason and the two are NOT reconciled.
 *
 *  ⚠️ AND STILL NO OFFICER NAME ANYWHERE IN IT (§4b, confirmed twice with the client). The
 *  service does not read the officer master at all — `arrangementPreview.test.ts` scans its
 *  source for `Officer` and `phone` and fails on either.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ForceMatrix = ({ duty }: { duty: PreviewDutyDto }) => {
  /*
   * The side of the matrix: every addressee any column asks for, in the order the columns
   * introduce them — `forceGrid.ts`'s own "first-mention order, so adding a division to one
   * post type makes one new row across the whole grid".
   */
  const rows = new Map<string, PreviewDivisionRowDto>();
  for (const post of duty.posts) {
    for (const row of post.divisions) if (!rows.has(row.allocKey)) rows.set(row.allocKey, row);
  }
  const addressees = [...rows.values()];

  /** `(allocKey, lineKey)` → the cell. Absent means this column does not ask this addressee. */
  const cells = new Map<string, PreviewDivisionRowDto>();
  for (const post of duty.posts) {
    for (const row of post.divisions) cells.set(`${row.allocKey}|${post.lineKey}`, row);
  }

  if (addressees.length === 0) {
    /* Every column is empty — §4g at the whole-table scale, which is a sentence rather than a
       table with one axis. */
    return <p className="text-sm text-muted-foreground">{PREVIEW_COPY.noDivisions}</p>;
  }

  return (
    /* ⚠️ CLAUDE.md §8: a wide table SCROLLS inside its own container; the dialog never does. */
    <div className="overflow-x-auto" data-testid="preview-matrix">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-border-strong">
            <th scope="col" className="w-40 py-1 pr-2 text-left font-medium text-muted-foreground">
              {PREVIEW_COPY.source}
            </th>
            {duty.posts.map((post) => (
              <th
                key={post.lineKey}
                scope="col"
                data-testid={`preview-col-${post.lineKey}`}
                className="px-2 py-1 text-right font-medium text-foreground"
                title={post.label}
              >
                {/*
                  ⚠️ [C2a §5a] A VEHICLE IS A COUNT — never "n of m named", which is a claim about
                  PEOPLE. That pass removed the officer treatment from the fill screen for exactly
                  this reason (a van cannot be staffed, so red-amber-green answers the wrong
                  question) and this header, added by §5, put it back on a second screen.
                  ⚠️ IT READS `kind`, NEVER THE `V:` PREFIX. The prefix is how `fillRef.util.ts`
                  MINTS a key; `kind` is what the line IS, and parsing the key is the
                  identity-from-a-position family §7 has corrected five times.
                */}
                {post.label}{' '}
                {post.kind === 'vehicle'
                  ? PREVIEW_COPY.vehicleCount(post.requiredCount)
                  : PREVIEW_COPY.postCount(post.namedCount, post.requiredCount)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {addressees.map((addressee) => (
            <tr
              key={addressee.allocKey}
              data-testid={`preview-row-${addressee.allocKey}`}
              className="border-b border-border"
            >
              <th scope="row" className="min-w-0 py-1 pr-2 text-left font-normal text-muted-foreground">
                <span className="block truncate">
                  {addressee.label}
                  {/* ⚠️ §4h — the delegated unit, where the line names one. */}
                  {addressee.unitLabel === null ? '' : ` · ${addressee.unitLabel}`}
                </span>
              </th>
              {duty.posts.map((post) => {
                const cell = cells.get(`${addressee.allocKey}|${post.lineKey}`);
                return (
                  <td
                    key={post.lineKey}
                    data-testid={`preview-cell-${addressee.allocKey}-${post.lineKey}`}
                    className="px-2 py-1 text-right tabular-nums text-foreground"
                  >
                    {/* ⚠️ An addressee this column does not ask for is BLANK, never `0 of 0` —
                        a zero here would read as a demand nobody has met.
                        ⚠️ [P7A §3] A vehicle cell is its required count alone — never `0 of 2`. */}
                    {cell
                      ? post.kind === 'vehicle'
                        ? PREVIEW_COPY.vehicleCell(cell.requiredCount)
                        : PREVIEW_COPY.namedOf(cell.namedCount, cell.requiredCount)
                      : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
